import { useCallback, useState } from 'react';
import { autoUpdate, flip, useFloating } from '@floating-ui/react-dom';
import { useSnapshot } from 'valtio';

import { cx } from '@libs/classnames';
import { useClickOutside } from '@libs/hooks/useClickOutside';
import { useOnEscape } from '@libs/hooks/useOnKeyDown';
import { ContextMenu, ContextMenuItem } from '@ui/components/ContextMenu/ContextMenu';
import { FancyBadge } from '@ui/components/FancyBadge';
import { CameraIcon } from '@ui/icons/CameraIcon';
import { KebabIcon } from '@ui/icons/KebabIcon';
import { MicIcon } from '@ui/icons/MicIcon';
import { PinIcon } from '@ui/icons/PinIcon';

import { Highlighted, RefereeDevice, RefereePlayer, Speaking } from '@features/video/types';
import { VideoPreview } from '@features/video/ui/VideoPreview';

import { NO_HOVER_PROXY, NO_SPEAKING_PROXY, setHighlighted } from './stores';
import { TileState } from './TileState';

import styles from './PlayerTile.module.css';

export type PlayerActions = {
  // вернуть удаленного из черного списка
  onRestore?: () => void;
  // выключить микрофон одного устройства; обратно включить может только игрок
  onMute?: (deviceId: string) => void;
  onKick?: () => void;
  onInvitePrivate?: () => void;
  // вернуть из приватной комнаты в общую
  onReturnToRoom?: () => void;
  // адрес страницы приватной комнаты игрока: ссылка, которую судья может скопировать
  // и переслать другому судье
  privateRoomHref?: string;
  onOpenGame?: (gameId: string) => void;
};

export type Props = PlayerActions & {
  className?: string;
  player: RefereePlayer;
  // устройство этой плитки; без него плитка показывает состояние игрока
  device?: RefereeDevice;
  // место под камеру, которую игрок должен был включить, но не включил
  missing?: boolean;
  // текст плитки игрока без устройств
  offlineText?: string;
  index?: number;
  // сколько всего плиток у игрока: при нескольких на каждой номер камеры,
  // на продолжениях имя серым
  count?: number;
  // горячие значения, подписка в плитке: над кем курсор (все плитки игрока подсвечены,
  // пока он над любой из них) и какие устройства говорят
  hoverProxy?: Highlighted;
  speakingProxy?: Speaking;
  pinned?: boolean;
  onPin?: () => void;
};

// Плитка одного устройства игрока на странице судьи: видео во всю плитку,
// имя под ним, закрепление и меню игрока поверх видео при наведении
export function PlayerTile({
  className,
  player,
  device,
  missing,
  offlineText = 'Не подключен',
  index = 0,
  count = 1,
  hoverProxy = NO_HOVER_PROXY,
  speakingProxy = NO_SPEAKING_PROXY,
  pinned,
  onPin,
  onRestore,
  onMute,
  onKick,
  onInvitePrivate,
  onReturnToRoom,
  privateRoomHref,
  onOpenGame,
}: Props) {
  const { name, playing, gameId, devices, removed, inPrivateRoom, privateReferees } = player;
  // читаем только свой ключ: перерисовка лишь на его смену
  const highlighted = !!useSnapshot(hoverProxy)[player.key];
  const speakingSnap = useSnapshot(speakingProxy);
  const isSpeaking = device !== undefined && !!speakingSnap[device.id];
  const isOffline = devices.length === 0;
  const isLive = !isOffline && !removed;
  // флаг привата стоит, а устройства еще здесь: игрок переходит
  const isSwitching = inPrivateRoom && isLive;
  const hasMenu = Boolean(onInvitePrivate || onReturnToRoom || onOpenGame || onKick);

  // Меню: пункты всегда одни и те же, неприменимые выключены — так не надо целиться
  const [isMenuOpen, setMenuOpen] = useState(false);
  const closeMenu = useCallback(() => setMenuOpen(false), []);
  const floating = useFloating({
    placement: 'bottom-end',
    strategy: 'fixed',
    whileElementsMounted: autoUpdate,
    middleware: [flip()],
  });
  const menuRef = useClickOutside<HTMLDivElement>(isMenuOpen ? closeMenu : undefined);
  useOnEscape(isMenuOpen ? closeMenu : undefined);

  const pick = (action: () => void) => () => {
    setMenuOpen(false);
    action();
  };

  // наведение ловит неподвижная обертка: сама плитка при подсветке сдвигается
  return (
    <div
      className={cx(styles.tile_wrap, className, { [styles.is_highlighted]: highlighted })}
      onMouseEnter={() => setHighlighted(hoverProxy, player.key)}
      onMouseLeave={() => setHighlighted(hoverProxy, null)}
    >
      <article
        className={cx(styles.tile, {
          [styles.is_idle]: !playing,
          [styles.is_removed]: removed,
          [styles.is_private]: inPrivateRoom,
          [styles.is_highlighted]: highlighted,
        })}
      >
        <div className={styles.tile__media}>
          {missing ? (
            <TileState text="Не подключена" />
          ) : device && isLive ? (
            <>
              <VideoPreview
                className={cx(styles.tile__device, { [styles.is_speaking]: isSpeaking })}
                stream={device.camOn ? device.stream : null}
                attach={device.camOn ? device.attach : undefined}
                placeholder={<CameraIcon off />}
              />
              {/* кнопка видна при наведении; выключенный микрофон виден всегда */}
              {(onMute || !device.micOn) && (
                <button
                  type="button"
                  className={cx(styles.tile__mic, { [styles.is_off]: !device.micOn })}
                  title={device.micOn ? 'Выключить микрофон' : 'Микрофон выключен'}
                  disabled={!device.micOn}
                  onClick={onMute && (() => onMute(device.id))}
                >
                  <MicIcon off={!device.micOn} />
                </button>
              )}
            </>
          ) : removed ? (
            <TileState text="Удален из комнаты">
              {onRestore && <FancyBadge onClick={onRestore}>Восстановить</FancyBadge>}
            </TileState>
          ) : inPrivateRoom ? (
            <TileState text="В приватной комнате" note={refereesNote(privateReferees)}>
              {privateRoomHref && (
                <FancyBadge href={privateRoomHref} inNewTab={false}>
                  Перейти
                </FancyBadge>
              )}
            </TileState>
          ) : (
            <TileState text={offlineText} />
          )}

          {/* играющие без пометок: они важнее всего, камеру не заслоняем; у остальных на каждой плитке */}
          {(!playing || isSwitching) && (
            <div className={styles.tile__labels}>
              {!playing && <span className={styles.tile__label}>Вне игры</span>}
              {isSwitching && <span className={styles.tile__label}>Переходит в приват</span>}
            </div>
          )}

          {/* закрепление и меню на каждой плитке игрока, чтобы не целиться в первую; видны
              при наведении. Свои кнопки, не IconButton: темная подложка нужна на любом фоне */}
          {(onPin || hasMenu) && (
            <div className={styles.tile__actions}>
              {onPin && (
                <button
                  type="button"
                  className={cx(styles.tile__pin, { [styles.is_active]: pinned })}
                  title={pinned ? 'Открепить' : 'Закрепить'}
                  onClick={onPin}
                >
                  <PinIcon />
                </button>
              )}
              {hasMenu && (
                <div
                  ref={(el) => {
                    menuRef.current = el;
                    floating.refs.setReference(el);
                  }}
                >
                  <button
                    type="button"
                    className={cx(styles.tile__kebab, { [styles.is_open]: isMenuOpen })}
                    title="Действия"
                    onClick={() => setMenuOpen((v) => !v)}
                  >
                    <KebabIcon />
                  </button>
                  {isMenuOpen && (
                    <ContextMenu
                      animated
                      className={styles.tile__menu}
                      style={floating.floatingStyles}
                      ref={floating.refs.setFloating}
                    >
                      {inPrivateRoom
                        ? onReturnToRoom && (
                            <ContextMenuItem onClick={pick(onReturnToRoom)}>
                              Вернуть в комнату
                            </ContextMenuItem>
                          )
                        : onInvitePrivate && (
                            // не подключенного можно перевести заранее: войдет сразу в приват
                            <ContextMenuItem disabled={removed} onClick={pick(onInvitePrivate)}>
                              Перевести в приват
                            </ContextMenuItem>
                          )}
                      {onOpenGame && (
                        <ContextMenuItem
                          disabled={!gameId}
                          onClick={gameId ? pick(() => onOpenGame(gameId)) : undefined}
                        >
                          Открыть партию
                        </ContextMenuItem>
                      )}
                      {onKick && (
                        <ContextMenuItem
                          textClassName={styles.tile__danger}
                          disabled={removed}
                          onClick={pick(onKick)}
                        >
                          Удалить из комнаты
                        </ContextMenuItem>
                      )}
                    </ContextMenu>
                  )}
                </div>
              )}
            </div>
          )}
        </div>

        {/* подпись под превью: на продолжениях имя серым; номер камеры, если плиток несколько */}
        <footer className={styles.tile__caption}>
          <span className={cx(styles.tile__name, { [styles.is_muted]: index > 0 })} title={name}>
            {name}
          </span>
          {count > 1 && <span className={styles.tile__index}>{index + 1}</span>}
        </footer>
      </article>
    </div>
  );
}

/*
    ____,-------------------------------,____
    \   |            Helpers            |   /
    /___|-------------------------------|___\
*/

// Кто из судей с игроком в привате; никого — он там ждет один
function refereesNote(names: string[] = []): string {
  if (names.length === 0) return 'Без судьи';
  return `${names.length === 1 ? 'Судья' : 'Судьи'}: ${names.join(', ')}`;
}
